import {
  Target,
  Compass,
  ShieldCheck,
  Users2,
  Briefcase,
  Lightbulb,
  Heart,
  Award,
  Crown,
  Building2,
  Network,
} from "lucide-react";
import { PolicyPageShell } from "@/components/PolicyHeader";

const CORE_VALUES = [
  { icon: ShieldCheck, t: "Integrity", d: "Honesty, transparency, accountability and ethical conduct in every transaction." },
  { icon: Heart, t: "Customer First", d: "Understanding needs, delivering quality service and building lifelong trust." },
  { icon: Award, t: "Professional Excellence", d: "Striving for excellence across every endeavour — from advisory to back-office." },
  { icon: Crown, t: "Leadership", d: "Developed through responsibility, accountability and commitment to people." },
  { icon: Users2, t: "Teamwork", d: "Collaboration across roles, departments and the Advisor / DSA / Franchise network." },
  { icon: Briefcase, t: "Compliance & Governance", d: "Strict adherence to laws, regulations and internal policies." },
  { icon: Lightbulb, t: "Innovation & Growth", d: "Adapting to a changing financial landscape with new tools and ideas." },
  { icon: Heart, t: "Respect & Dignity", d: "Treating every employee, partner and customer with respect and fairness." },
];

const HIERARCHY = [
  { lvl: "L1", role: "Board of Directors", scope: "Strategic direction, policy approval & risk oversight" },
  { lvl: "L2", role: "Chairman & Managing Director", scope: "Highest leadership authority + day-to-day execution" },
  { lvl: "L3", role: "National Head", scope: "Nationwide business development & expansion" },
  { lvl: "L4", role: "State Head", scope: "State-level operations, recruitment & coordination" },
  { lvl: "L5", role: "Regional Manager", scope: "Multiple districts within a region" },
  { lvl: "L6", role: "District Head", scope: "District operations & branch expansion" },
  { lvl: "L7", role: "Taluka Head", scope: "Taluka territories, advisor recruitment" },
  { lvl: "L8", role: "Area Manager", scope: "Customer acquisition across multiple branches" },
  { lvl: "L9", role: "Branch Manager", scope: "Branch operations, customer service" },
];

const NETWORKS = [
  {
    icon: Briefcase,
    title: "Advisor Network",
    nodes: ["Chief Advisor", "Senior Financial Advisor", "Financial Advisor", "Associate Advisor"],
  },
  {
    icon: Network,
    title: "DSA Network",
    nodes: ["National DSA Coordinator", "State DSA Coordinator", "District DSA Coordinator", "DSA Partner", "Associate DSA"],
  },
  {
    icon: Building2,
    title: "Franchise & Business Partners",
    nodes: ["Master Franchise Partner", "Regional Franchise Partner", "Business Partner", "Business Associate"],
  },
];

const COMMITTEES = [
  {
    name: "Audit Committee",
    d: "Oversees financial reporting, internal controls and independent audits to ensure transparency and accuracy.",
  },
  {
    name: "Compliance Committee",
    d: "Ensures full compliance with RBI, SEBI, MCA and Gujarat Money Lenders Act regulations.",
  },
  {
    name: "Risk Management Committee",
    d: "Identifies and mitigates risks while sustaining strong risk-adjusted portfolio performance.",
  },
  {
    name: "Ethics Committee",
    d: "Promotes the highest ethical standards, integrity and anti-bribery practices across the organisation.",
  },
  {
    name: "CSR Committee",
    d: "Drives social initiatives in education, health and tribal development across Tapi & Surat districts.",
  },
  {
    name: "Technology Governance Committee",
    d: "Ensures data security, cybersecurity and smooth functioning of Investor & Partner Portals.",
  },
  {
    name: "Executive Management Committee",
    d: "Leads strategic planning and day-to-day operations while protecting investor and stakeholder interests.",
  },
];

export default function Governance() {
  return (
    <PolicyPageShell
      testId="governance-page"
      eyebrow="Corporate Governance Charter"
      title={
        <>
          Governance, <em className="not-italic text-[#D4AF37]">Vision</em> &amp;{" "}
          <span className="font-script italic text-[#F0D788]">Values</span>
        </>
      }
      lead="Strong governance creates sustainable success. This charter — derived from the Final Master Corporate Handbook 2026 — defines our purpose, principles and reporting structure across every level of the organisation."
    >
      {/* Vision & Mission */}
      <section className="grid md:grid-cols-2 gap-6 mb-16">
        <div
          data-testid="governance-vision"
          className="rounded-3xl p-7 md:p-9 border border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/[0.08] to-transparent"
        >
          <Target size={28} className="text-[#F0D788]" />
          <h2 className="mt-4 font-display text-2xl text-white">Our Vision</h2>
          <p className="mt-3 text-white/75 text-sm leading-relaxed">
            To create financial growth and opportunities for individuals, families and enterprises
            through ethical financial services — driving financial inclusion, wealth creation,
            customer satisfaction and a pan-India distribution network.
          </p>
        </div>
        <div
          data-testid="governance-mission"
          className="rounded-3xl p-7 md:p-9 border border-[#D4AF37]/25 bg-gradient-to-br from-emerald-500/[0.05] to-transparent"
        >
          <Compass size={28} className="text-[#F0D788]" />
          <h2 className="mt-4 font-display text-2xl text-white">Our Mission</h2>
          <ul className="mt-3 space-y-2 text-white/75 text-sm leading-relaxed list-disc pl-5 marker:text-[#D4AF37]">
            <li>Provide honest guidance and professional financial services to every customer.</li>
            <li>Empower advisors and DSA partners with growth opportunities &amp; training.</li>
            <li>Build a culture of integrity, compliance and customer-first thinking.</li>
            <li>Support communities through financial inclusion and education.</li>
          </ul>
        </div>
      </section>

      {/* Core values */}
      <section className="mb-16">
        <h2 className="font-display text-3xl text-center">Our 8 Core Values</h2>
        <p className="mt-3 text-center text-white/60 max-w-2xl mx-auto text-sm">
          The foundation of every decision, conversation and customer interaction.
        </p>
        <div className="mt-9 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CORE_VALUES.map((v, i) => (
            <div
              key={v.t}
              data-testid={`governance-value-${i + 1}`}
              className="rounded-2xl p-5 border border-white/10 bg-white/[0.03] hover:border-[#D4AF37]/40 transition"
            >
              <v.icon size={22} className="text-[#F0D788]" />
              <div className="mt-3 font-display text-lg text-white">{v.t}</div>
              <p className="mt-1.5 text-white/55 text-xs leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Hierarchy */}
      <section className="mb-16">
        <div className="mb-7">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">Authority Structure</div>
          <h2 className="font-display text-3xl mt-2">9-Tier Corporate Hierarchy</h2>
          <p className="mt-1 text-white/60 text-sm">
            From the Board to the Branch Manager — each level has clearly defined authority and reporting lines.
          </p>
        </div>
        <div className="rounded-2xl border border-white/10 overflow-hidden">
          <table data-testid="governance-hierarchy" className="w-full text-sm">
            <thead className="bg-white/[0.05] text-white/55 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left p-4 w-20">Level</th>
                <th className="text-left p-4">Role</th>
                <th className="text-left p-4">Scope &amp; Responsibility</th>
              </tr>
            </thead>
            <tbody>
              {HIERARCHY.map((h) => (
                <tr key={h.lvl} className="border-t border-white/5">
                  <td className="p-4 font-display text-[#F0D788]">{h.lvl}</td>
                  <td className="p-4 font-medium text-white">{h.role}</td>
                  <td className="p-4 text-white/65">{h.scope}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Network structures */}
      <section className="mb-16">
        <h2 className="font-display text-3xl text-center">Distribution Networks</h2>
        <p className="mt-3 text-center text-white/60 max-w-2xl mx-auto text-sm">
          Three parallel growth ladders that operate alongside the corporate hierarchy.
        </p>
        <div className="mt-9 grid md:grid-cols-3 gap-5">
          {NETWORKS.map((n) => (
            <div
              key={n.title}
              data-testid={`governance-network-${n.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-2xl p-6 border border-[#D4AF37]/25 bg-white/[0.03]"
            >
              <n.icon size={24} className="text-[#F0D788]" />
              <h3 className="mt-3 font-display text-lg text-white">{n.title}</h3>
              <ol className="mt-3 space-y-1.5">
                {n.nodes.map((node, i) => (
                  <li key={node} className="text-sm text-white/70 flex items-start gap-2">
                    <span className="w-5 h-5 grid place-items-center rounded-full bg-[#D4AF37]/15 text-[#F0D788] text-[10px] shrink-0 mt-0.5">{i + 1}</span>
                    {node}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </section>

      {/* Governance committees */}
      <section className="rounded-3xl bg-white/[0.04] border border-[#D4AF37]/25 p-7 md:p-10">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">Specialist Oversight</div>
        <h2 className="font-display text-3xl mt-2">Governance Committees</h2>
        <p className="mt-2 text-white/60 text-sm max-w-2xl">
          Noble Infinity Asset Management Pvt Ltd maintains the highest standards of governance
          through specialised committees that ensure transparency, risk management and long-term
          value creation for investors.
        </p>
        <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {COMMITTEES.map((c) => (
            <div
              key={c.name}
              data-testid={`governance-committee-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-2xl p-5 bg-white/[0.03] border-l-4 border-[#D4AF37] hover:bg-white/[0.06] transition"
            >
              <h3 className="font-display text-lg text-[#F0D788]">{c.name}</h3>
              <p className="mt-2 text-white/70 text-sm leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-white/70 italic text-sm border-l-2 border-[#D4AF37] pl-4">
          &ldquo;Strong Governance Creates Sustainable Success&rdquo; — Corporate Governance Philosophy.
        </p>
      </section>
    </PolicyPageShell>
  );
}
